import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Divider,
} from '@mui/material';
import { ArrowBack, SmartToy, Computer, Code, Schedule, Storage, MoreVert, Label } from '@mui/icons-material';
import { projectsAPI, optimizedApi } from '../services/api';
import { Project } from '../types';

interface ServerIpAddress {
  _id: string;
  ipAddress: string;
  projectCount: number;
  lastActivity: string;
  projects: string[];
  customName?: string;
}

interface ProjectListProps {
  category: 'robots' | 'servers';
  onProjectSelect: (project: Project) => void;
  onBack: () => void;
}

const ProjectList: React.FC<ProjectListProps> = React.memo(({ category, onProjectSelect, onBack }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [serverIpAddresses, setServerIpAddresses] = useState<ServerIpAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<{ ipAddress: string; type: 'robot' | 'server' } | null>(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchData = useCallback(async (preserveOptimisticUpdate?: { ipAddress: string; customName: string; type: 'robot' | 'server' }) => {
    try {
      setLoading(true);
      if (category === 'servers') {
        // Clear cache to ensure we get fresh data from backend
        optimizedApi.clearCache('/projects/servers/ip-addresses');
        
        // Fetch IP addresses for servers
        const response = await projectsAPI.getServerIpAddresses();
        
        // Debug: Log the raw API response
        console.log('Raw API response:', JSON.stringify(response.data, null, 2));
        
        const serversWithCustomNames = response.data.map((server: any) => {
          // IMPORTANT: Use ONLY the database value - never fallback to localStorage
          // The database is the source of truth
          const storageKey = `customName_server_${server.ipAddress}`;
          
          // Debug: Log each server's data BEFORE processing
          console.log(`[BEFORE] Server IP: ${server.ipAddress}, DB customName: "${server.customName}", Type: ${typeof server.customName}`);
          
          // Preserve optimistic update if this is the item we just saved
          if (preserveOptimisticUpdate && 
              preserveOptimisticUpdate.type === 'server' && 
              preserveOptimisticUpdate.ipAddress === server.ipAddress) {
            // Check if server has updated with the new value
            const serverCustomName = server.customName ? String(server.customName) : '';
            const optimisticValue = preserveOptimisticUpdate.customName || '';
            
            // If server value matches our optimistic value, use it (server has updated)
            // If server value is different/empty, preserve our optimistic value
            if (serverCustomName !== optimisticValue && optimisticValue) {
              // Server hasn't updated yet, preserve our optimistic value
              localStorage.setItem(storageKey, optimisticValue);
              console.log(`[MERGE] Preserving optimistic update for ${server.ipAddress}: "${optimisticValue}" (server has: "${serverCustomName}")`);
              return {
                ...server,
                customName: optimisticValue
              };
            }
          }
          
          // Use database value ONLY - ignore localStorage completely
          // Check if server.customName exists explicitly (not null/undefined)
          const hasDbCustomName = server.customName !== null && server.customName !== undefined && server.customName !== '';
          const customName = hasDbCustomName ? String(server.customName) : undefined;
          
          // Update localStorage to match database value
          if (hasDbCustomName) {
            localStorage.setItem(storageKey, String(server.customName));
          } else {
            // Clear localStorage if database has no custom name
            localStorage.removeItem(storageKey);
          }
          
          // Debug: Log AFTER processing
          console.log(`[AFTER] Server IP: ${server.ipAddress}, Final customName: "${customName}"`);
          
          return {
            ...server,
            customName: customName
          };
        });
        
        // Debug: Log the final mapped data
        console.log('Final servers with custom names:', JSON.stringify(serversWithCustomNames.map(s => ({ 
          ip: s.ipAddress, 
          customName: s.customName,
          _id: s._id 
        })), null, 2));
        
        setServerIpAddresses(serversWithCustomNames);
      } else {
        // Fetch projects for robots
        const response = await projectsAPI.getProjects(category === 'robots');
        
        // Debug: Log the raw API response
        console.log('Raw robots API response:', JSON.stringify(response.data.map((p: any) => ({
          projectName: p.projectName,
          ipAddress: p.ipAddress,
          customName: p.customName
        })), null, 2));
        
        const projectsWithCustomNames = response.data.map((project: any) => {
          // IMPORTANT: Use ONLY the database value - never fallback to localStorage
          // The database is the source of truth
          const storageKey = `customName_robot_${project.ipAddress}`;
          
          // Preserve optimistic update if this is the item we just saved
          if (preserveOptimisticUpdate && 
              preserveOptimisticUpdate.type === 'robot' && 
              preserveOptimisticUpdate.ipAddress === project.ipAddress) {
            // Check if server has updated with the new value
            const serverCustomName = project.customName ? String(project.customName) : '';
            const optimisticValue = preserveOptimisticUpdate.customName || '';
            
            // If server value matches our optimistic value, use it (server has updated)
            // If server value is different/empty, preserve our optimistic value
            if (serverCustomName !== optimisticValue && optimisticValue) {
              // Server hasn't updated yet, preserve our optimistic value
              localStorage.setItem(storageKey, optimisticValue);
              console.log(`[MERGE] Preserving optimistic update for ${project.ipAddress}: "${optimisticValue}" (server has: "${serverCustomName}")`);
              return {
                ...project,
                customName: optimisticValue
              };
            }
          }
          
          // Debug: Log each project's data BEFORE processing
          console.log(`[BEFORE] Project: ${project.projectName}, IP: ${project.ipAddress}, DB customName: "${project.customName}"`);
          
          // Use database value ONLY - ignore localStorage completely
          // Check if project.customName exists explicitly (not null/undefined)
          const hasDbCustomName = project.customName !== null && project.customName !== undefined && project.customName !== '';
          const customName = hasDbCustomName ? String(project.customName) : undefined;
          
          // Update localStorage to match database value
          if (hasDbCustomName) {
            localStorage.setItem(storageKey, String(project.customName));
          } else {
            // Clear localStorage if database has no custom name
            localStorage.removeItem(storageKey);
          }
          
          // Debug: Log AFTER processing
          console.log(`[AFTER] Project: ${project.projectName}, IP: ${project.ipAddress}, Final customName: "${customName}"`);
          
          return {
            ...project,
            customName: customName
          };
        });
        
        // Debug: Log projects after processing
        console.log('Projects after processing:', projectsWithCustomNames.map((p: any) => ({
          projectName: p.projectName,
          ipAddress: p.ipAddress,
          customName: p.customName
        })));
        
        setProjects(projectsWithCustomNames);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to fetch ${category === 'servers' ? 'server IP addresses' : 'projects'}`);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group robots by IP address
  const groupRobotsByIpAddress = (robots: Project[]) => {
    const grouped = robots.reduce((acc, robot) => {
      const ipAddress = robot.ipAddress || 'Unknown IP';
      if (!acc[ipAddress]) {
        acc[ipAddress] = {
          ipAddress,
          robots: [],
          totalCommits: 0,
          lastActivity: '',
          latestCommit: {
            commitHash: '',
            commitMessage: '',
            authorName: '',
            commitTimestamp: '',
            branchName: ''
          },
          customName: robot.customName,
          // Track all custom names for this IP to find the most common one
          customNames: new Map<string, number>()
        };
      }
      
      acc[ipAddress].robots.push(robot);
      acc[ipAddress].totalCommits += robot.totalCommits;
      
      // Track custom names frequency
      if (robot.customName) {
        const customNamesMap = acc[ipAddress].customNames;
        if (customNamesMap) {
          const count = customNamesMap.get(robot.customName) || 0;
          customNamesMap.set(robot.customName, count + 1);
        }
      }
      
      // Keep the most recent activity
      if (!acc[ipAddress].lastActivity || new Date(robot.lastActivity) > new Date(acc[ipAddress].lastActivity)) {
        acc[ipAddress].lastActivity = robot.lastActivity;
      }
      
      // Keep the most recent commit
      if (!acc[ipAddress].latestCommit.commitTimestamp || 
          new Date(robot.latestCommit.commitTimestamp) > new Date(acc[ipAddress].latestCommit.commitTimestamp)) {
        acc[ipAddress].latestCommit = robot.latestCommit;
      }
      
      return acc;
    }, {} as Record<string, {
      ipAddress: string;
      robots: Project[];
      totalCommits: number;
      lastActivity: string;
      latestCommit: any;
      customName?: string;
      customNames?: Map<string, number>;
    }>);
    
    // After grouping, determine the most common custom name for each IP
    const groups = Object.values(grouped).map(group => {
      // First, check if there's an IP-level custom name stored (user-saved custom name for this IP)
      const ipLevelCustomNameKey = `customName_robot_${group.ipAddress}`;
      const ipLevelCustomName = localStorage.getItem(ipLevelCustomNameKey);
      
      if (ipLevelCustomName) {
        // If user has set a custom name for this IP, use it (highest priority)
        console.log(`[GROUPING] IP ${group.ipAddress}: Using IP-level custom name: "${ipLevelCustomName}"`);
        group.customName = ipLevelCustomName;
      } else if (group.customNames && group.customNames.size > 0) {
        // Otherwise, find the most common custom name from projects
        let maxCount = 0;
        let mostCommonName = '';
        group.customNames.forEach((count, name) => {
          if (count > maxCount) {
            maxCount = count;
            mostCommonName = name;
          }
        });
        
        // If there's a tie, prefer the custom name from the most recently active robot
        if (maxCount > 0) {
          // Check if there are multiple custom names with the same count
          const sameCountNames: string[] = [];
          group.customNames.forEach((count, name) => {
            if (count === maxCount) {
              sameCountNames.push(name);
            }
          });
          
          if (sameCountNames.length > 1) {
            // Multiple custom names with same frequency - use the one from the most recent robot
            let mostRecentRobot: Project | null = null;
            for (const robot of group.robots) {
              if (sameCountNames.includes(robot.customName || '')) {
                if (!mostRecentRobot || new Date(robot.lastActivity) > new Date(mostRecentRobot.lastActivity)) {
                  mostRecentRobot = robot;
                }
              }
            }
            if (mostRecentRobot?.customName) {
              mostCommonName = mostRecentRobot.customName;
            }
          } else {
            mostCommonName = sameCountNames[0] || mostCommonName;
          }
        }
        
        console.log(`[GROUPING] IP ${group.ipAddress}: Using most common custom name: "${mostCommonName}" (appears ${maxCount} times)`);
        group.customName = mostCommonName || group.customName;
      }
      
      // Clean up the customNames map before returning
      delete group.customNames;
      
      return group;
    });
    
    // Debug: Log grouped robots
    console.log('Grouped robots:', groups.map((g: any) => ({
      ipAddress: g.ipAddress,
      customName: g.customName,
      robotCount: g.robots.length
    })));
    
    return groups;
  };

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, ipAddress: string, type: 'robot' | 'server') => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedItem({ ipAddress, type });
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedItem here - let it persist until dialog is closed
  };

  // Handle add/update server name click
  const handleAddServerName = () => {
    const item = selectedItem;
    
    // Close menu first
    setAnchorEl(null);
    
    if (item) {
      // Get existing custom name if available
      let existingName = '';
      if (item.type === 'server') {
        const server = serverIpAddresses.find(s => s.ipAddress === item.ipAddress);
        existingName = server?.customName || '';
      } else {
        const groupedRobots = groupRobotsByIpAddress(projects);
        const robot = groupedRobots.find(r => r.ipAddress === item.ipAddress);
        existingName = robot?.customName || '';
      }
      
      // Also check localStorage as fallback
      if (!existingName) {
        const storageKey = `customName_${item.type}_${item.ipAddress}`;
        existingName = localStorage.getItem(storageKey) || '';
      }
      
      setCustomName(existingName);
      setDialogOpen(true);
    }
  };

  // Handle save custom name
  const handleSaveCustomName = async () => {
    if (!selectedItem) {
      return;
    }

    // Save selectedItem and customName to local variables before clearing state
    const itemToSave = { ...selectedItem };
    const nameToSave = customName;

    try {
      setSaving(true);
      
      // Optimistically update the UI immediately
      const storageKey = `customName_${itemToSave.type}_${itemToSave.ipAddress}`;
      
      // Update localStorage first so grouping logic picks it up immediately
      if (nameToSave) {
        localStorage.setItem(storageKey, nameToSave);
      } else {
        localStorage.removeItem(storageKey);
      }
      
      if (itemToSave.type === 'server') {
        // Update server state immediately
        setServerIpAddresses(prevServers => 
          prevServers.map(server => 
            server.ipAddress === itemToSave.ipAddress
              ? { ...server, customName: nameToSave || undefined }
              : server
          )
        );
      } else {
        // Update robot projects state immediately
        setProjects(prevProjects => 
          prevProjects.map(project => 
            project.ipAddress === itemToSave.ipAddress
              ? { ...project, customName: nameToSave || undefined }
              : project
          )
        );
      }
      
      // Close dialog immediately to show the update
      setDialogOpen(false);
      setCustomName('');
      setSelectedItem(null);
      
      // Try to call API to save custom name
      try {
        await projectsAPI.saveCustomName(itemToSave.ipAddress, nameToSave, itemToSave.type);
        
        // Clear cache to force fresh data fetch
        if (itemToSave.type === 'server') {
          optimizedApi.clearCache('/projects/servers/ip-addresses');
        } else {
          optimizedApi.clearCache('/projects');
        }
        
        // Update localStorage to keep it in sync with database
        if (nameToSave) {
          localStorage.setItem(storageKey, nameToSave);
        } else {
          localStorage.removeItem(storageKey);
        }
        
        // Wait a moment for server to update, then refresh data from API
        // Preserve optimistic update when merging in case server hasn't updated yet
        setTimeout(() => {
          fetchData({ ipAddress: itemToSave.ipAddress, customName: nameToSave, type: itemToSave.type }).catch(err => {
            console.error('Error refreshing data after save:', err);
            // On error, we still show success since optimistic update worked
          });
        }, 500); // Small delay to ensure server has updated
        
        setSnackbar({ 
          open: true, 
          message: 'Custom name saved successfully!', 
          severity: 'success' 
        });
      } catch (apiError) {
        console.warn('API call failed, using localStorage fallback:', apiError);
        // Fallback to localStorage if API is not available
        if (nameToSave) {
          localStorage.setItem(storageKey, nameToSave);
        } else {
          localStorage.removeItem(storageKey);
        }
        
        setSnackbar({ 
          open: true, 
          message: 'Custom name saved locally!', 
          severity: 'success' 
        });
      }
    } catch (error: any) {
      console.error('Error saving custom name:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to save custom name. Please try again.', 
        severity: 'error' 
      });
      
      // Revert optimistic update on error by refreshing data
      fetchData().catch(err => {
        console.error('Error refreshing data after save error:', err);
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Don't show full screen loading - let the page structure render
  // if (loading) {
  //   return (
  //     <Box
  //       display="flex"
  //       justifyContent="center"
  //       alignItems="center"
  //       minHeight="100vh"
  //       sx={{ background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)',
        p: 4,
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            mb: 2,
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          Back to Categories
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
              mr: 2,
            }}
          >
            {category === 'robots' ? (
              <SmartToy sx={{ fontSize: 32, color: 'white' }} />
            ) : (
              <Computer sx={{ fontSize: 32, color: 'white' }} />
            )}
          </Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
            {category === 'robots' ? 'Robot List' : 'Server List'}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          {loading ? 'Loading...' : (category === 'servers'
            ? `${serverIpAddresses.length} ${serverIpAddresses.length === 1 ? 'server' : 'servers'} found`
            : `${category === 'robots' ? groupRobotsByIpAddress(projects).length : projects.length} ${category === 'robots' ? 'robot IP addresses' : 'projects'} found`
          )}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#8b5cf6' }} />
          <Typography sx={{ ml: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
            Loading {category === 'robots' ? 'robots' : 'servers'}...
          </Typography>
        </Box>
      )}

      {!loading && category === 'servers' ? (
        // Render server IP addresses
        serverIpAddresses.length === 0 ? (
          <Card sx={{
            p: 4,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }}>
            <Typography variant="h6" sx={{ color: '#334155' }}>
              No server IP addresses found
            </Typography>
          </Card>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {serverIpAddresses.map((server, index) => {
              // Debug: Log what's being rendered
              console.log(`Rendering server card ${index}: IP=${server.ipAddress}, customName=${server.customName}, _id=${server._id}`);
              return (
              <Box key={`${server._id}-${server.ipAddress}`}>
                <Fade in timeout={500 + index * 100}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      position: 'relative',
                    }}
                    onClick={() => {
                      // Create a mock project object for IP addresses
                      const mockProject: Project = {
                        _id: server._id,
                        projectName: server.ipAddress,
                        isRobot: false,
                        ipAddress: server.ipAddress,
                        customName: server.customName,
                        latestCommit: {
                          commitHash: '',
                          commitMessage: `Server with ${server.projectCount} projects`,
                          authorName: 'System',
                          commitTimestamp: server.lastActivity,
                          branchName: 'main'
                        },
                        totalCommits: server.projectCount,
                        lastActivity: server.lastActivity
                      };
                      onProjectSelect(mockProject);
                    }}
                  >
                    {/* Three-dot menu button */}
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                      onClick={(e) => handleMenuOpen(e, server.ipAddress, 'server')}
                      size="small"
                    >
                      <MoreVert sx={{ color: '#475569', fontSize: 20 }} />
                    </IconButton>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {/* Header Section */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                            }}
                          >
                            <Storage sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="h5" 
                              fontWeight="bold" 
                              sx={{ 
                                color: '#1a202c',
                                mb: 0.5,
                                fontSize: '1.25rem'
                              }}
                            >
                              {server.customName || 'Server'}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#64748b', 
                                fontSize: '0.875rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              {server.ipAddress}
                            </Typography>
                        </Box>
                      </Box>

                        <Chip
                          label="Server"
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            height: '24px',
                            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
                          }}
                        />
                      </Box>

                      <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

                      {/* Server Info Section */}
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#6b7280', 
                            mb: 1.5, 
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Server Details
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule sx={{ fontSize: 16, color: '#8b5cf6' }} />
                            <Typography variant="body2" sx={{ color: '#1a202c', fontWeight: 500 }}>
                              {server.projectCount} {server.projectCount === 1 ? 'project' : 'projects'} running
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule sx={{ fontSize: 16, color: '#8b5cf6' }} />
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Last activity: {formatDate(server.lastActivity)}
                          </Typography>
                        </Box>
                      </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Box>
              );
            })}
          </Box>
        )
      ) : null}

      {!loading && category === 'robots' && (
        // Render robot projects grouped by IP address
        (() => {
          const groupedRobots = groupRobotsByIpAddress(projects);
          
          return groupedRobots.length === 0 ? (
            <Card sx={{
              p: 4,
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            }}>
              <Typography variant="h6" sx={{ color: '#334155' }}>
                No robots found
              </Typography>
            </Card>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 3,
              }}
            >
              {groupedRobots.map((group, index) => (
                <Box key={group.ipAddress}>
                  <Fade in timeout={500 + index * 100}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                        position: 'relative',
                      }}
                      onClick={() => {
                        // Create a mock project object for the IP address that contains all robots
                        const mockProject: Project = {
                          _id: group.ipAddress,
                          projectName: group.ipAddress,
                          isRobot: true,
                          ipAddress: group.ipAddress,
                          robotNames: group.robots.map(robot => 
                            robot.robotNames && robot.robotNames.length > 0 ? robot.robotNames[0] : robot.projectName
                          ),
                          latestCommit: group.latestCommit,
                          totalCommits: group.totalCommits,
                          lastActivity: group.lastActivity,
                          customName: group.customName
                        };
                        onProjectSelect(mockProject);
                      }}
                    >
                      {/* Three-dot menu button */}
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 10,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        onClick={(e) => handleMenuOpen(e, group.ipAddress, 'robot')}
                        size="small"
                      >
                        <MoreVert sx={{ color: '#475569', fontSize: 20 }} />
                      </IconButton>

                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        {/* Header Section */}
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2,
                                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                              }}
                            >
                              <Code sx={{ fontSize: 24, color: 'white' }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="h5" 
                                fontWeight="bold" 
                                sx={{ 
                                  color: '#1a202c',
                                  mb: 0.5,
                                  fontSize: '1.25rem'
                                }}
                              >
                                {group.customName || 'Robot Group'}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#64748b', 
                                  fontSize: '0.875rem',
                                  fontFamily: 'monospace'
                                }}
                              >
                                {group.ipAddress}
                              </Typography>
                          </Box>
                        </Box>

                          <Chip
                            label="Robot"
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              height: '24px',
                              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                            }}
                          />
                        </Box>

                        <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

                        {/* Robot Names Section */}
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6b7280', 
                              mb: 1.5, 
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            Robot Names
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {(() => {
                              // Get unique robot names
                              const uniqueRobotNames = Array.from(new Set(
                                group.robots.map(robot => 
                                  robot.robotNames && robot.robotNames.length > 0 
                                    ? robot.robotNames[0] 
                                    : robot.projectName
                                )
                              ));
                              
                              return uniqueRobotNames.map((robotName, index) => (
                                <Chip
                                  key={index}
                                  label={robotName}
                                  size="small"
                                  sx={{
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem',
                                    height: '28px',
                                    boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                />
                              ));
                            })()}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                </Box>
              ))}
            </Box>
          );
        })()
      )}

      {/* Three-dot Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
              fontSize: '0.95rem',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
              },
            },
          },
        }}
      >
        <MenuItem 
          onClick={handleAddServerName}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
          }}
        >
          <Label sx={{ mr: 1.5, fontSize: 20, color: '#8b5cf6' }} />
          <Typography 
            sx={{ 
              fontWeight: 'bold',
              color: '#1a202c',
              fontSize: '0.95rem'
            }}
          >
            {selectedItem?.type === 'robot' ? 'Update Robot Name' : 'Update Server Name'}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Add Server Name Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={(event, reason) => {
          // Only close on backdrop click or escape key, not on button clicks
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            setDialogOpen(false);
            setCustomName('');
            setSelectedItem(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={saving}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {selectedItem?.type === 'robot' ? 'Update Robot Name' : 'Update Server Name'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label={selectedItem?.type === 'robot' ? 'Robot Name' : 'Server Name'}
            fullWidth
            variant="outlined"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={`Enter a custom name for this ${selectedItem?.type === 'robot' ? 'robot' : 'server'}`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#8b5cf6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8b5cf6',
                },
              },
            }}
          />
          {selectedItem && (
            <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
              IP Address: {selectedItem.ipAddress}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setDialogOpen(false);
              setCustomName('');
              setSelectedItem(null);
            }}
            variant="outlined"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCustomName}
            variant="contained"
            disabled={saving}
            sx={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              },
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ProjectList;